'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var mlGsd = require('ml-gsd');
var max = _interopDefault(require('ml-array-max'));
var IsotopicDistribution = _interopDefault(require('isotopic-distribution'));
var binarySearch = _interopDefault(require('binary-search'));
var numSort = require('num-sort');
var mfParser = require('mf-parser');
var arrayMean = _interopDefault(require('ml-array-mean'));
var Regression = _interopDefault(require('ml-regression-polynomial'));
var jcampconverter = require('jcampconverter');
var xyParser = require('xy-parser');
var netcdfJSON = _interopDefault(require('netcdf-gcms'));
var mzData = _interopDefault(require('mzdata'));

/**
 * Modifies the time applying the conversion function
 * @param {Array<number>} originalTime - Original list of time values
 * @param {function(number)} conversionFunction - Function that given a number computes the new value
 * @return {Array<number>} - Modified list of time values
 */
function rescaleTime(originalTime, conversionFunction) {
  return originalTime.map(conversionFunction);
}

/**
 * Filter the chromatogram based on a callback
 * The callback will take a time
 * @param {Chromatogram} chromatogram - GC/MS chromatogram where make the peak picking
 * @param {function(number, number)} callback
 * @param {object} [options] - options object
 * @param {boolean} [options.copy = false] - return a copy of the original object
 * @return {Chromatogram} - Modified chromatogram
 */
function filter(chromatogram, callback, options = {}) {
  const { copy = false } = options;
  let chrom;
  if (copy) {
    chrom = chromatogram.copy();
  } else {
    chrom = chromatogram;
  }

  let times = chrom.getTimes();
  let newTimes = [];
  let indexToKeep = [];
  for (let i = 0; i < times.length; i++) {
    if (callback(i, times[i])) {
      indexToKeep.push(i);
      newTimes.push(times[i]);
    }
  }
  chrom.setTimes(newTimes);

  for (let key of chrom.getSerieNames()) {
    let serie = chrom.getSerie(key);
    serie.keep(indexToKeep);
  }

  return chrom;
}

/**
 * Class allowing to manage a Serie
 */
class Serie {
  constructor(array, dimension, options = {}) {
    let { meta = {} } = options;
    if (new.target === Serie) {
      throw new Error('You need to create either a 1D or 2D serie');
    }
    this.data = array;
    this.dimension = dimension;
    this.meta = meta;
    this.name = '';
  }

  is1D() {
    return this.dimension === 1;
  }

  is2D() {
    return this.dimension === 2;
  }

  toJSON() {
    return {
      data: this.data,
      meta: this.meta
    };
  }

  /**
   * Specify an array of index to keep
   * @param {Array} array
   */
  keep(array) {
    let newData = [];
    for (let i of array) {
      newData.push(this.data[i]);
    }
    this.data = newData;
  }
}

/**
 * Class allowing to manage a Serie
 */
class Serie1D extends Serie {
  constructor(array) {
    super(array, 1);
  }
}

/**
 * Class allowing to manage a 2D Serie
 */
class Serie2D extends Serie {
  constructor(array) {
    super(array, 2);
  }
}

function isArray(object) {
  return toString.call(object).endsWith('Array]');
}

function serieFromArray(array) {
  // need to check if it is a 1D or 2D array (or 3D ?)
  if (!isArray(array)) {
    throw new TypeError(
      'Serie.fromArray requires as parameter an array of numbers or array'
    );
  }

  if (array.length === 0 || typeof array[0] === 'number') {
    return new Serie1D(array);
  }

  if (!isArray(array[0])) {
    throw new TypeError(
      'Serie.fromArray requires as parameter an array of numbers or array'
    );
  }

  return new Serie2D(array);
}

/**
 * Parse from a JSON element to a new Chromatogram
 * @param {object} json - Result from the toJSON method
 * @return {Chromatogram} - New parsed Chromatogram
 */
function fromJSON(json) {
  let series = json.series;
  let times = json.times;
  let chromatogram = new Chromatogram(times);

  if (Array.isArray(series)) {
    for (let i = 0; i < series.length; i++) {
      chromatogram.addSerie(series[i].name, series[i].data);
    }
  } else {
    for (let key of Object.keys(series)) {
      chromatogram.addSerie(key, series[key].data, {
        meta: series[key].meta
      });
    }
  }
  return chromatogram;
}

/**
 * Apply the GSD peak picking algorithm
 * @param {Chromatogram} chromatogram - GC/MS chromatogram where make the peak picking
 * @param {object} [options] - Options object
 * @param {number} [options.heightFilter = 2] - Filter all objects that are bellow `heightFilter` times the median of the height
 * @param {string} [options.serieName = 'tic'] - Serie to do the peak picking
 * @param {object} [options.broadenPeaks = {}] - Options to broadenPeaks
 * @param {number} [options.broadenPeaks.factor = 1] - factor to enlarge
 * @param {boolean} [options.broadenPeaks.overlap = false] - Prevent overlap kf false
 * @return {Array<object>} - List of GSD objects
 */
function getPeaks(chromatogram, options = {}) {
  const { heightFilter = 2, serieName = 'tic', broadenPeaks = {} } = options;

  let serie = chromatogram.getSerie(serieName);
  if (!serie) {
    throw new Error(`"${serieName}" serie not founded`);
  }
  serie = serie.data;
  let times = chromatogram.getTimes();

  // first peak selection
  let peakList = mlGsd.gsd(times, serie, {
    noiseLevel: 0,
    realTopDetection: false,
    smoothY: true,
    sgOptions: { windowSize: 5, polynomial: 2 },
    heightFactor: 2,
    boundaries: true
  });

  peakList.sort(
    (a, b) => a.right.index - a.left.index - (b.right.index - b.left.index)
  );
  let medianDotsWidth = peakList[Math.floor((peakList.length - 1) / 2)];
  medianDotsWidth = medianDotsWidth.right.index - medianDotsWidth.left.index;

  // reset parameters
  if (medianDotsWidth < 5) {
    medianDotsWidth = 5;
  }
  if (medianDotsWidth % 2 === 0) {
    medianDotsWidth -= 1;
  }

  // second peak selection
  peakList = mlGsd.gsd(times, serie, {
    noiseLevel: 0,
    realTopDetection: false,
    smoothY: true,
    sgOptions: { windowSize: medianDotsWidth, polynomial: 2 },
    heightFactor: 2,
    boundaries: true
  });
  peakList.sort((a, b) => a.height - b.height);

  // filter height by factor
  let medianHeight = peakList[Math.floor((peakList.length - 1) / 2)].height;
  peakList = peakList.filter((val) => val.height > medianHeight * heightFilter);

  var factor = broadenPeaks.factor === undefined ? 1 : broadenPeaks.factor;
  var overlap =
    broadenPeaks.overlap === undefined ? false : broadenPeaks.overlap;
  mlGsd.post.broadenPeaks(peakList, {
    factor,
    overlap
  });

  return peakList;
}

/**
 * Calculate tic
 * @param {Chromatogram} chromatogram - GC/MS chromatogram where make the peak picking
 * @return {Array} - Calculated tic
 */
function calculateTic(chromatogram) {
  let ms = chromatogram.getSerie('ms');
  if (!ms) {
    throw new Error('The mass serie must be defined');
  }
  var massSpectra = ms.data;
  var tic = [];
  for (var massSpectrum of massSpectra) {
    tic.push(massSpectrum[1].reduce((a, b) => a + b, 0));
  }

  return tic;
}

/**
 * Calculate the number of points of each related information
 * @param {Chromatogram} chromatogram - GC/MS chromatogram where make the peak picking
 * @param {string} serieName - name of the serie
 * @return {Array} - Calculated length of the 2D array
 */
function calculateLength(chromatogram, serieName) {
  let serie2D = chromatogram.getSerie(serieName);
  var spectra = serie2D.data;
  var length = spectra.map((spectrum) => spectrum[0].length);
  return length;
}

/**
 * Calculate bpc
 * @param {Chromatogram} chrom - GC/MS chromatogram where make the peak picking
 * @return {Array} - Calculated bpc
 */
function calculateBpc(chrom) {
  let ms = chrom.getSerie('ms');
  if (!ms) {
    throw new Error('The mass serie must be defined');
  }
  var massSpectra = ms.data;
  var bpc = [];
  for (var massSpectrum of massSpectra) {
    if (massSpectrum[1].length > 0) {
      bpc.push(max(massSpectrum[1]));
    } else {
      bpc.push(0);
    }
  }

  return bpc;
}

/**
 * Calculate tic
 * @param {Chromatogram} chromatogram - GC/MS chromatogram where make the peak picking
 * @param {number} targetMass - mass for which to extract the spectrum
 * @param {object} [options={}]
 * @param {number} [options.error=0.5] - Allowed error around the targetMass
 * @return {Array} - Calculated mass for targetMass
 */
function calculateForMass(chromatogram, targetMass, options = {}) {
  if (isNaN(targetMass)) {
    throw Error('calculateForMass: targetMass must be defined and a number');
  }
  const { error = 0.5 } = options;
  let ms = chromatogram.getSerie('ms');
  if (!ms) {
    throw Error('calculateForMass: the mass serie must be defined');
  }
  var massSpectra = ms.data;
  var result = new Array(massSpectra.length).fill(0);
  for (let i = 0; i < massSpectra.length; i++) {
    let massSpectrum = massSpectra[i];
    for (let j = 0; j < massSpectrum[0].length; j++) {
      if (Math.abs(massSpectrum[0][j] - targetMass) <= error) {
        result[i] += massSpectrum[1][j];
      }
    }
  }

  return result;
}

/**
 * Calculate tic
 * @param {Chromatogram} chromatogram - GC/MS chromatogram where make the peak picking
 * @param {string} targetMF - mass for which to extract the spectrum
 * @param {object} [options={}]
 * @param {number} [options.error=0.5] - Allowed error around the targetMF
 * @param {number} [options.ionizations='H+'] - List of allowed ionisation
 * @return {Array} - Calculated mass for targetMass
 */
function calculateForMF(chromatogram, targetMF, options = {}) {
  if (typeof targetMF !== 'string') {
    throw Error('calculateForMF: targetMF must be defined and a string');
  }
  const { error = 0.5 } = options;

  let ms = chromatogram.getSerie('ms');
  if (!ms) {
    throw Error('calculateForMF: the mass serie must be defined');
  }

  var masses = new IsotopicDistribution(targetMF, {
    ionizations: options.ionizations.replace(/ /g, '')
  })
    .getParts()
    .map((entry) => entry.ms.em);

  var massSpectra = ms.data;
  var result = new Array(massSpectra.length).fill(0);
  for (let targetMass of masses) {
    for (let i = 0; i < massSpectra.length; i++) {
      let massSpectrum = massSpectra[i];
      for (let j = 0; j < massSpectrum[0].length; j++) {
        if (Math.abs(massSpectrum[0][j] - targetMass) <= error) {
          result[i] += massSpectrum[1][j];
        }
      }
    }
  }
  return result;
}

const ascValue = (a, b) => (a - b);

/**
 * Returns information for the closest time
 * @param {number} time - Retention time
 * @param {Array<number>} times - Time array
 * @return {{index: number, timeBefore: number, timeAfter: number, timeClosest: number, safeIndexBefore: number, safeIndexAfter: number}}
 */
function getClosestTime(time, times) {
  let position = binarySearch(times, time, ascValue);

  if (position < 0) {
    // the value doesn't exists in the array
    position = -position - 1;

    let safeIndexBefore = position === 0 ? 0 : position - 1;
    if (position > (times.length - 1)) {
      position = times.length - 1;
      safeIndexBefore = times.length - 1;
    }
    const safeIndexAfter = position;

    let difference = times[position] - time;
    if (difference > 0.5) {
      position = safeIndexBefore;
    }

    return {
      index: position,
      timeBefore: times[safeIndexBefore],
      timeAfter: times[safeIndexAfter],
      timeClosest: times[position],
      safeIndexBefore: safeIndexBefore,
      safeIndexAfter: safeIndexAfter
    };
  } else {
    // the value exists in the array
    return {
      index: position,
      timeBefore: times[position],
      timeAfter: times[position],
      timeClosest: times[position],
      safeIndexBefore: position,
      safeIndexAfter: position
    };
  }
}

function baselineCorrection(total, base, kind) {
  if (total === 0) {
    return {
      integral: 0,
      base: {
        start: { height: 0 },
        end: { height: 0 }
      }
    };
  }
  switch (kind) {
    case 'trapezoid':
      return {
        integral: total - ((base.end.time - base.start.time) * (base.end.height + base.start.height) / 2),
        base
      };
    case 'min':
      if (base.end.height > base.start.height) {
        base.end.height = base.start.height;
        return {
          integral: total - ((base.end.time - base.start.time) * base.start.height),
          base
        };
      } else {
        base.start.height = base.end.height;
        return {
          integral: total - ((base.end.time - base.start.time) * base.end.height),
          base
        };
      }
    default:
      throw new Error(`Unknown baseline method "${kind}"`);
  }
}

/**
 * Returns a mass spectrum that is the integration of all the spectra in a specific range of time
 * @param {Chromatogram} chromatogram
 * @param {string} name - Name of the serie to integrate
 * @param {Array<Array<number>>} ranges - [[from1, to1], [from2, to2], ...]
 * @param {object} [options = {}] - Options object
 * @param {string|boolean} [options.baseline] - Applies baseline correction (trapezoid, min)
 * @return {[]}
 */
function integrate(chromatogram, name, ranges, options = {}) {
  const {
    baseline = false
  } = options;

  if (!Array.isArray(ranges)) {
    throw new Error('ranges must be an array of type [[from,to]]');
  }
  if (ranges.length === 0) {
    return undefined;
  }

  if (!Array.isArray(ranges[0]) || ranges[0].length !== 2) {
    throw new Error('ranges must be an array of type [[from,to]]');
  }

  chromatogram.requiresSerie(name);
  let serie = chromatogram.series[name];
  if (serie.dimension !== 1) {
    throw new Error('the serie is not of dimension 1');
  }

  const time = chromatogram.getTimes();
  let results = [];

  for (let fromTo of ranges) {
    let from = fromTo[0];
    let to = fromTo[1];
    let fromIndex = getClosestTime(from, time).safeIndexBefore;
    let toIndex = getClosestTime(to, time).safeIndexAfter;

    results.push(_integrate(time, serie, from, to, fromIndex, toIndex, baseline));
  }

  return results;
}

function _integrate(time, serie, from, to, fromIndex, toIndex, baseline) {
  let total = 0;
  let base = {};
  for (let i = fromIndex; i < toIndex; i++) {
    let timeStart = time[i];
    let timeEnd = time[i + 1];
    let heightStart = serie.data[i];
    if (i === fromIndex) { // need to check the exact starting point
      heightStart = serie.data[i] + (serie.data[i + 1] - serie.data[i]) * (from - timeStart) / (timeEnd - timeStart);
      base.start = { height: heightStart, time: from };
      timeStart = from;
    }

    let heightEnd = serie.data[i + 1];
    if (i === toIndex - 1) {
      heightEnd = serie.data[i] + (serie.data[i + 1] - serie.data[i]) * (to - timeStart) / (timeEnd - timeStart);
      base.end = { height: heightEnd, time: to };
      timeEnd = to;
    }
    total += (timeEnd - timeStart) * (heightStart + heightEnd) / 2;
  }

  if (baseline) {
    var ans = baselineCorrection(total, base, baseline);
    return {
      integral: ans.integral,
      from: {
        time: from,
        index: fromIndex,
        baseline: ans.base.start.height
      },
      to: {
        time: to,
        index: toIndex,
        baseline: ans.base.end.height
      }
    };
  } else {
    return {
      integral: total,
      from: {
        time: from,
        index: fromIndex,
        baseline: 0
      },
      to: {
        time: to,
        index: toIndex,
        baseline: 0
      }
    };
  }
}

/**
 * Returns a mass spectrum that is the integration of all the spectra in a specific range of time
 * @param {Chromatogram} chromatogram
 * @param {string} name - Name of the serie to integrate
 * @param {Array<Array<number>>} ranges - [[from1, to1], [from2, to2], ...]
 * @param {object} [options = {}] - Options object
 * @param {object} [options.algorithm = 'slot'] - Decision for merging the peaks
 * @param {object} [options.delta = 1] - Parameter for merging the peaks
 * @return {[]}
 */
function merge(chromatogram, name, ranges, options = {}) {
  const {
    algorithm = 'slot',
    delta = 1
  } = options;

  if (!Array.isArray(ranges) || !Array.isArray(ranges[0])) {
    throw new Error('ranges must be an array of type [[from,to]]');
  }

  chromatogram.requiresSerie(name);
  let serie = chromatogram.series[name];
  if (serie.dimension !== 2) {
    throw new Error('The serie is not of dimension 2');
  }

  const time = chromatogram.getTimes();
  let results = [];

  for (let fromTo of ranges) {
    let from = fromTo[0];
    let to = fromTo[1];
    let fromIndex = getClosestTime(from, time).safeIndexBefore;
    let toIndex = getClosestTime(to, time).safeIndexAfter;

    results.push({
      serie: _merge(serie, fromIndex, toIndex, delta, algorithm),
      from: {
        time: from,
        index: fromIndex
      },
      to: {
        time: to,
        index: toIndex
      }
    });
  }

  return results;
}

function _merge(serie, fromIndex, toIndex, delta, algorithm) {
  switch (algorithm) {
    case 'slot':
      return slot(fromIndex, toIndex, serie, delta);
    case 'centroid':
      return centroid(fromIndex, toIndex, serie, delta);
    default:
      throw new Error(`Unknown algorithm "${algorithm}"`);
  }
}

function slot(fromIndex, toIndex, serie, slot) {
  let massDictionary = {};

  for (var i = fromIndex; i <= toIndex; i++) {
    for (var j = 0; j < serie.data[i][0].length; j++) {
      // round the mass value
      var x = serie.data[i][0][j];
      let mass = x + slot / 2 - (x + slot / 2) % slot;

      // add the mass value to the dictionary
      if (massDictionary[mass]) {
        massDictionary[mass] += serie.data[i][1][j];
      } else {
        massDictionary[mass] = serie.data[i][1][j];
      }
    }
  }

  const massList = Object.keys(massDictionary).map((val) => Number(val)).sort(numSort.asc);
  let integral = [
    new Array(massList.length),
    new Array(massList.length)
  ];

  for (var k = 0; k < massList.length; k++) {
    integral[0][k] = Number(massList[k]);
    integral[1][k] = massDictionary[massList[k]];
  }
  return integral;
}

function centroid(fromIndex, toIndex, serie, slot) {
  var integral = [[], []];

  for (var i = fromIndex; i <= toIndex; i++) {
    integral = mergeCentroids(integral, serie.data[i], slot);
  }
  return integral;
}

function mergeCentroids(previous, data, slot) {
  var leftIndex = 0;
  var rightIndex = 0;
  var merged = [[], []];
  var weightedMass = [[], []];
  var size = 0;

  while ((leftIndex < previous[0].length) && (rightIndex < data[0].length)) {
    if (previous[0][leftIndex] <= data[0][rightIndex]) {
      // append first(left) to result
      if ((size === 0) || (previous[0][leftIndex] - merged[0][size - 1] > slot)) {
        weightedMass[0].push(previous[0][leftIndex] * previous[1][leftIndex]);
        weightedMass[1].push(previous[1][leftIndex]);
        merged[0].push(previous[0][leftIndex]);
        merged[1].push(previous[1][leftIndex++]);
        size++;
      } else {
        weightedMass[0][size - 1] += previous[0][leftIndex] * previous[1][leftIndex];
        weightedMass[1][size - 1] += previous[1][leftIndex];
        merged[0][size - 1] = previous[0][leftIndex];
        merged[1][size - 1] += previous[1][leftIndex++];
      }
    } else {
      // append first(right) to result
      if ((size === 0) || (data[0][rightIndex] - merged[0][size - 1] > slot)) {
        weightedMass[0].push(data[0][rightIndex] * data[1][rightIndex]);
        weightedMass[1].push(data[1][rightIndex]);
        merged[0].push(data[0][rightIndex]);
        merged[1].push(data[1][rightIndex++]);
        size++;
      } else {
        weightedMass[0][size - 1] += data[0][rightIndex] * data[1][rightIndex];
        weightedMass[1][size - 1] += data[1][rightIndex];
        merged[0][size - 1] = data[0][rightIndex];
        merged[1][size - 1] += data[1][rightIndex++];
      }
    }
  }

  while (leftIndex < previous[0].length) {
    // append first(left) to result
    if ((size === 0) || (previous[0][leftIndex] - merged[0][size - 1] > slot)) {
      weightedMass[0].push(previous[0][leftIndex] * previous[1][leftIndex]);
      weightedMass[1].push(previous[1][leftIndex]);
      merged[0].push(previous[0][leftIndex]);
      merged[1].push(previous[1][leftIndex++]);
      size++;
    } else {
      weightedMass[0][size - 1] += previous[0][leftIndex] * previous[1][leftIndex];
      weightedMass[1][size - 1] += previous[1][leftIndex];
      merged[0][size - 1] = previous[0][leftIndex];
      merged[1][size - 1] += previous[1][leftIndex++];
    }
  }

  while (rightIndex < data[0].length) {
    // append first(right) to result
    if ((size === 0) || (data[0][rightIndex] - merged[0][size - 1] > slot)) {
      weightedMass[0].push(data[0][rightIndex] * data[1][rightIndex]);
      weightedMass[1].push(data[1][rightIndex]);
      merged[0].push(data[0][rightIndex]);
      merged[1].push(data[1][rightIndex++]);
      size++;
    } else {
      weightedMass[0][size - 1] += data[0][rightIndex] * data[1][rightIndex];
      weightedMass[1][size - 1] += data[1][rightIndex];
      merged[0][size - 1] = data[0][rightIndex];
      merged[1][size - 1] += data[1][rightIndex++];
    }
  }

  for (var i = 0; i < merged[0].length; i++) {
    merged[0][i] = weightedMass[0][i] / weightedMass[1][i];
  }

  return merged;
}

/**
 * Calculates the Kovats retention index for a mass spectra of a n-alkane
 * @param {object} ms - An mass spectra object
 * @param {Array<number>} ms.x - Array of masses
 * @param {Array<number>} ms.y - Array of intensities
 * @return {number} - Kovats retention index
 */
function kovats(ms) {
  let mass = ms.x;
  let massMol = [];
  const targets = [43, 57, 71, 85];

  for (let i = 0; i < mass.length; i++) {
    if ((mass[i] - 2) % 14 === 0) {
      massMol.push(mass[i]);
    }
  }
  if (massMol.length === 0) {
    return 0;
  }

  let kovatsIndex = 0;
  for (var m = 0; m < massMol.length; m++) {
    let candidate = true;
    for (var t = 0; t < targets.length; t++) {
      candidate = candidate
                && (mass.indexOf(targets[t]) !== -1)
                && ((mass.indexOf(massMol[m] - targets[t]) !== -1)
                || (mass.indexOf(massMol[m] - targets[t] + 1) !== -1)
                || (mass.indexOf(massMol[m] - targets[t] - 1) !== -1));
    }
    if (candidate) {
      kovatsIndex = 100 * (massMol[m] - 2) / 14;
    }
  }
  return kovatsIndex;
}

/**
 * Filters a mass object
 * @param {object} massXYObject - Object with x and y data
 * @param {Array<number>} massXYObject.x - Array of mass values
 * @param {Array<number>} massXYObject.y - Array of abundance values
 * @param {object} options - Options for the integral filtering
 * @param {number} [options.thresholdFactor = 0] - Every peak that it's bellow the main peak times this factor fill be removed (when is 0 there's no filter)
 * @param {number} [options.maxNumberPeaks = Number.MAX_VALUE] - Maximum number of peaks for each mass spectra (when is Number.MAX_VALUE there's no filter)
 * @param {number} [options.groupWidth = 0] - When find a max can't be another max in a radius of this size
 * @return {object} - Object with filtered x and y data
 */
function massFilter(massXYObject, options = {}) {
  const { thresholdFactor = 0, maxNumberPeaks = Number.MAX_VALUE, groupWidth = 0 } = options;

  let max$$1 = -1;
  let massList = new Array(massXYObject.x.length);
  for (let i = 0; i < massXYObject.x.length; ++i) {
    massList[i] = {
      x: massXYObject.x[i],
      y: massXYObject.y[i]
    };

    if (massXYObject.y[i] > max$$1) {
      max$$1 = massXYObject.y[i];
    }
  }

  // filters based in thresholdFactor
  max$$1 *= thresholdFactor;
  let filteredList = massList.filter((val) => val.y > max$$1);

  // filters based in maxNumberPeaks
  if (filteredList.length > maxNumberPeaks || groupWidth !== 0) {
    filteredList.sort((a, b) => b.y - a.y);

    // filters based in groupWidth
    filteredList = moreDistinct(filteredList, maxNumberPeaks, groupWidth);

    filteredList.sort((a, b) => a.x - b.x);
  }

  let ans = {
    x: new Array(filteredList.length),
    y: new Array(filteredList.length)
  };
  for (let i = 0; i < filteredList.length; ++i) {
    ans.x[i] = filteredList[i].x;
    ans.y[i] = filteredList[i].y;
  }

  return ans;
}

/**
 * Filters based in groupWidth
 * @ignore
 * @param {Array<object>} list - Sorted list of XY-objects to be filtered
 * @param {number} maxNumberPeaks - Maximum number of peaks for each mass spectra
 * @param {number} groupWidth - When find a max can't be another max in a radius of this size
 * @return {Array<object>} - List of XY-objects filtered
 */
function moreDistinct(list, maxNumberPeaks, groupWidth) {
  let len = 0;
  if (maxNumberPeaks > list.length) {
    maxNumberPeaks = list.length;
  }
  let filteredList = new Array(maxNumberPeaks);

  for (let i = 0; (i < list.length) && (len < maxNumberPeaks); ++i) {
    let outRange = true;
    for (let j = 0; j < len && outRange; ++j) {
      outRange = outRange && !((list[i].x > (filteredList[j].x - groupWidth)) && (list[i].x < (filteredList[j].x + groupWidth)));
    }
    if (outRange) {
      filteredList[len++] = list[i];
    }
  }
  filteredList.length = len;

  return filteredList;
}

/**
 * Integrate MS spectra of a peak list
 * @param {Array<object>} peakList - List of GSD objects
 * @param {Array<object>} sampleMS - MS array of GC spectra
 * @param {object} options - Options for the integral filtering
 * @param {number} [options.thresholdFactor = 0] - Every peak that it's bellow the main peak times this factor fill be removed (when is 0 there's no filter)
 * @param {number} [options.maxNumberPeaks = Number.MAX_VALUE] - Maximum number of peaks for each mass spectra (when is Number.MAX_VALUE there's no filter)
 * @param {number} [options.groupWidth = 0] - When find a max can't be another max in a radius of this size
 * @param {number} [options.slot = 1] - When to merge two mass values intensities
 * @param {number} [options.method = 'slot'] - Mass combination method
 * @return {Array<object>} - List of GSD objects with an extra 'ms' field with the integrated MS spectra
 */
function massInPeaks(peakList, sampleMS, options = {}) {
  const {
    thresholdFactor = 0,
    maxNumberPeaks = Number.MAX_VALUE,
    groupWidth = 0,
    slot = 1,
    method = 'slot'
  } = options;

  // integrate MS
  for (let i = 0; i < peakList.length; ++i) {
    var serie = { dimension: 2, data: sampleMS };
    var integral = _merge(serie, peakList[i].left.index, peakList[i].right.index, slot, method);
    var msSum = {
      x: integral[0],
      y: integral[1]
    };

    if (maxNumberPeaks || thresholdFactor || groupWidth) {
      msSum = massFilter(msSum, { maxNumberPeaks, thresholdFactor, groupWidth });
    }
    peakList[i].ms = msSum;
  }

  return peakList;
}

/**
 * Calculates the table of Kovats indexes for the reference spectra
 * @param {Chromatogram} reference - Reference spectra
 * @param {object} [options = {}] - Options object
 * @param {number} [options.heightFilter = 100] - Filter all objects that are below heightFilter times the median of the height
 * @param {number} [options.thresholdFactor = 0.005] - Every peak that is below the main peak times this factor will be removed (when is 0 there's no filter)
 * @param {number} [options.maxNumberPeaks = 40] - Maximum number of peaks for each mass spectra (when is Number.MAX_VALUE there's no filter)
 * @param {number} [options.groupWidth = 5] - When find a max can't be another max in a radius of this size
 * @return {{kovatsIndexes:Array<object>,peaks:Array<object>}} - Time and value for the Kovats index
 */
function getKovatsTable(reference, options = {}) {
  const {
    heightFilter = 100,
    thresholdFactor = 0.005,
    maxNumberPeaks = 40,
    groupWidth = 5
  } = options;

  // Peak picking
  let peaks = getPeaks(reference, { heightFilter });
  /* istanbul ignore next */
  peaks = peaks.sort((a, b) => a.index - b.index);

  // integrate mass in the peaks
  let ms = reference.getSerie('ms').data;
  let integratedMs = massInPeaks(peaks, ms, { thresholdFactor, maxNumberPeaks, groupWidth });

  var kovatsIndexes = new Array(integratedMs.length);
  for (var i = 0; i < integratedMs.length; i++) {
    kovatsIndexes[i] = {
      time: integratedMs[i].x,
      value: kovats(integratedMs[i].ms)
    };
  }

  return {
    kovatsIndexes,
    peaks
  };
}

const ascValue$1 = (a, b) => (a.value - b.value);
const ascTime = (a, b) => (a.time - b.time);

/**
 * Returns a function that allows to convert from time to Kovats or from Kovats to time
 * @param {Array<object>} kovatsConversionTable - List of time-kovats from the reference
 * @param {object} [options = {}] - Options object
 * @param {boolean} [options.revert = false] - True for convert from Kovats to time, false otherwise
 * @return {function(number)} - One parameter function that convert to one dimension to the other
 */
function kovatsConversionFunction(kovatsConversionTable, options = {}) {
  const { revert = false } = options;

  if (revert) {
    const values = kovatsConversionTable.sort(ascValue$1);

    return (index) => {
      let position = binarySearch(values, { value: index }, ascValue$1);

      if (position < 0) {
        position = ~position;

        // handle extreme cases
        if (position === 0 || position === values.length) {
          return 0;
        }

        let smallerAlcane = values[position - 1].time;
        let largerAlcane = values[position].time;
        return (index - values[position - 1].value) * (largerAlcane - smallerAlcane) / 100
                    + smallerAlcane;
      } else {
        return values[position].time;
      }
    };
  } else {
    const times = kovatsConversionTable.sort(ascTime);

    return (time) => {
      let position = binarySearch(times, { time }, ascTime);

      if (position < 0) {
        position = ~position;

        // handle extreme cases
        if (position === 0 || position === times.length) {
          return 0;
        }

        let smallerAlcane = times[position - 1].time;
        let largerAlcane = times[position].time;
        return 100 * (time - smallerAlcane) / (largerAlcane - smallerAlcane)
                    + times[position - 1].value;
      } else {
        return times[position].value;
      }
    };
  }
}

/**
 * Calculates the table of Kovats indexes for the reference spectra
 * @param {Chromatogram} reference - Reference spectra
 * @param {object} [options = {}] - Options object
 * @param {number} [options.heightFilter = 100] - Filter all objects that are below heightFilter times the median of the height
 * @param {number} [options.thresholdFactor = 0.005] - Every peak that is below the main peak times this factor will be removed (when is 0 there's no filter)
 * @param {number} [options.maxNumberPeaks = 40] - Maximum number of peaks for each mass spectra (when is Number.MAX_VALUE there's no filter)
 * @param {number} [options.groupWidth = 5] - When find a max can't be another max in a radius of this size
 * @param {boolean} [options.revert = false] - True for convert from Kovats to time, false otherwise
 * @return {{conversionFunction:function(number),kovatsIndexes:Array<object>,peaks:Array<object>}} - Time and value for the Kovats index
 */
function getKovatsRescale(reference, options) {
  let kovatsTable = getKovatsTable(reference, options);
  let conversionFunction = kovatsConversionFunction(kovatsTable.kovatsIndexes, { revert: options.revert });

  return {
    conversionFunction: conversionFunction,
    kovatsIndexes: kovatsTable.kovatsIndexes,
    peaks: kovatsTable.peaks
  };
}

/**
 * Recalculates series for GC/MS with lock mass
 * @param {string|Array<string>} mf - Reference molecular formula(s)
 * @param {object} [options = {}] - Options object
 * @param {boolean} [options.oddReference = true] - Mass reference it's in the odd position
 * @param {number} [options.maxShift = 0.1] - Maximum allowed shift
 * @param {boolean} [options.usePreviousIfNotFound = true] - If not found we use the previous value
 * @return {object} this
 */
function applyLockMass(mf, options = {}) {
  const { oddReference = true, maxShift = 0.1 } = options;

  // allows mf as string or array
  if (typeof mf === 'string') {
    mf = [mf];
  }

  // calculate the mass reference values
  const referenceMass = mf.map((mf) => {
    let info = new mfParser.MF(mf).getInfo();
    return info.observedMonoisotopicMass || info.monoisotopicMass;
  });

  var ms = this.getSerie('ms');
  if (!ms) {
    throw new Error('The "ms" serie must be defined');
  }
  ms = ms.data;

  // check where is the reference values
  var referenceIndexShift = Number(oddReference);
  var msIndexShift = Number(!oddReference);
  const newSize = ms.length >> 1;
  var referencesCount = new Array(referenceMass.length).fill(0);

  // applying the changes for all the spectra
  let previousValidDifference = Number.MAX_VALUE;
  let usingPreviousValidDifference = false;
  for (var i = 0; i < newSize; i++) {
    var massIndex = 2 * i + msIndexShift;
    var referenceIndex = 2 * i + referenceIndexShift;

    // calculate the difference between theory and experimental (the smallest)
    var difference = Number.MAX_VALUE;
    var closestIndex = -1;
    for (var j = 0; j < referenceMass.length; j++) {
      for (var k = 0; k < ms[referenceIndex][0].length; k++) {
        if (
          Math.abs(difference) >
          Math.abs(referenceMass[j] - ms[referenceIndex][0][k])
        ) {
          difference = referenceMass[j] - ms[referenceIndex][0][k];
          closestIndex = j;
        }
      }
    }
    if (
      Math.abs(difference) > maxShift &&
      Math.abs(previousValidDifference) < maxShift
    ) {
      difference = previousValidDifference;
      usingPreviousValidDifference = true;
    } else {
      usingPreviousValidDifference = false;
    }
    // apply identified lock mass
    if (Math.abs(difference) < maxShift) {
      previousValidDifference = difference;
      if (!usingPreviousValidDifference) {
        if (closestIndex !== -1) {
          referencesCount[closestIndex] += 1;
        }
      }
      for (var m = 0; m < ms[massIndex][0].length; m++) {
        ms[massIndex][0][m] += difference;
      }
    }
  }

  var referenceUsed = {
    total: newSize,
    totalFound: referencesCount.reduce((prev, current) => current + prev, 0)
  };
  for (var r = 0; r < referenceMass.length; r++) {
    referenceUsed[mf[r]] = referencesCount[r];
  }
  referenceUsed.percent =
    (referenceUsed.totalFound / referenceUsed.total) * 100;

  // remove the time and the mass spectra that contains the reference
  this.filter((index) => index % 2 !== referenceIndexShift);

  return { chrom: this, referenceUsed };
}

function meanFilter(chromatogram, serieName, options = {}) {
  const {
    factor = 2
  } = options;

  var serie = chromatogram.getSerie(serieName);
  var filtered = [];
  for (var i = 0; i < serie.data.length; i++) {
    filtered.push(applyFilter(serie.data[i], factor));
  }

  return serieFromArray(filtered);
}

function applyFilter(serie, factor) {
  const meanIntensity = factor * arrayMean(serie[1]);
  var filtered = [[], []];
  for (var i = 0; i < serie[0].length; i++) {
    if (serie[1][i] > meanIntensity) {
      filtered[0].push(serie[0][i]);
      filtered[1].push(serie[1][i]);
    }
  }
  return filtered;
}

function percentageFilter(chromatogram, serieName, options = {}) {
  const {
    percentage = 0.1
  } = options;

  var serie = chromatogram.getSerie(serieName);
  var filtered = [];

  for (var i = 0; i < serie.data.length; i++) {
    filtered.push(applyFilter$1(serie.data[i], percentage));
  }

  return serieFromArray(filtered);
}

function applyFilter$1(serie, percentage) {
  var basePeak;
  try {
    basePeak = max(serie[1]);
  } catch (e) {
    basePeak = 0;
  }
  var filtered = [[], []];
  for (var i = 0; i < serie[0].length; i++) {
    if (serie[1][i] > percentage * basePeak) {
      filtered[0].push(serie[0][i]);
      filtered[1].push(serie[1][i]);
    }
  }
  return filtered;
}

/**
 * Create an object of Chromatogram
 * @return {object}
 */
function toJSON() {
  return {
    times: this.times,
    series: this.series
  };
}

/**
 * Returns the closest mass spectrum to a specific retention time
 * @param {string} name - Serie name
 * @param {number} rt - Retention time
 * @return {{rt: number, index: number, data: Array}}
 */
function getClosestData(name, rt) {
  this.requiresSerie(name);
  let closest = this.getClosestTime(rt);
  return {
    rt: closest.timeClosest,
    index: closest.index,
    data: this.getSerie(name).data[closest.index]
  };
}

/**
 * Class allowing to store time / ms (ms) series
 * It allows also to store simple time a trace
 * @class Chromatogram
 * @param {Array<number>} times - Time serie
 * @param {object} series - A map of series with name and the Serie object
 */
class Chromatogram {
  constructor(times, series) {
    this.series = {};
    this.times = [];
    if (times) {
      if (!isArray(times)) {
        throw new TypeError('Times must be an array');
      }
      this.times = times;
    } else {
      throw new Error('The time serie is mandatory');
    }
    if (series) {
      this.addSeries(series);
    }
  }

  get length() {
    return this.times.length;
  }

  /**
   * Find the serie giving the name
   * @param {string} name - name of the serie
   * @return {object} - Object with an array of data, dimensions of the elements in the array and name of the serie
   */
  getSerie(name) {
    return this.series[name];
  }

  getSerieNames() {
    return Object.keys(this.series);
  }

  hasMass() {
    return this.hasSerie('ms');
  }

  /**
   * Delete a serie
   * @param {string} name - Name of the serie
   */
  deleteSerie(name) {
    if (!this.hasSerie(name)) {
      throw new Error(`The serie "${name}" does not exist`);
    } else {
      delete this.series[name];
    }
  }

  /**
   * Add new series
   * @param {object} series - Object with an array of data, dimensions of the elements in the array and name of the serie
   * @param {object} [options = {}] - Object with an array of data, dimensions of the elements in the array and name of the serie
   */
  addSeries(series, options = {}) {
    if (typeof series !== 'object' || Array.isArray(series)) {
      throw new TypeError('data must be an object containing arrays of series');
    }
    for (const key of Object.keys(series)) {
      this.addSerie(key, series[key], options);
    }
  }

  /**
   * Add a new serie
   * @param {string} name - Name of the serie to add
   * @param {Array} array - Object with an array of data, dimensions of the elements in the array and name of the serie
   * @param {object} [options = {}] - Options object
   * @param {boolean} [options.force = false] - Force replacement of existing serie
   */
  addSerie(name, array, options = {}) {
    if (this.hasSerie(name) && !options.force) {
      throw new Error(`A serie with name "${name}" already exists`);
    }
    if (this.times.length !== array.length) {
      throw new Error('The array size is not the same as the time size');
    }
    this.series[name] = serieFromArray(array);
    this.series[name].name = name;
  }

  /**
   * Returns true if the serie name exists
   * @param {string} name - Name of the serie to check
   * @return {boolean}
   */
  hasSerie(name) {
    return typeof this.series[name] !== 'undefined';
  }

  /**
   * Throws if the serie does not exists
   * @param {string} name - Name of the serie to check
   */
  requiresSerie(name) {
    if (!this.hasSerie(name)) {
      throw new Error(`The serie "${name}" does not exist`);
    }
  }

  /**
   * Returns the first time value
   * @return {number} - First time value
   */
  get firstTime() {
    return this.times[0];
  }

  /**
   * Returns the last time value
   * @return {number} - Last time value
   */
  get lastTime() {
    return this.times[this.length - 1];
  }

  /**
   * Returns the time values
   * @return {Array<number>} - Time values
   */
  getTimes() {
    return this.times;
  }

  /**
   * Assign the time values
   * @param {Array<number>} times - New time values
   */
  setTimes(times) {
    this.times = times;
  }

  /**
   * Modifies the time applying the conversion function
   * @param {function(number)} conversionFunction
   * @return {Chromatogram}
   */
  rescaleTime(conversionFunction) {
    this.times = rescaleTime(this.times, conversionFunction);
    return this;
  }

  /**
   * Will filter the entries based on the time
   * You can either use the index of the actual time
   * @param {function(number, number)} callback
   * @param {object} [options] - options object
   * @param {boolean} [options.copy = false] - return a copy of the original object
   * @return {Chromatogram}
   */
  filter(callback, options) {
    return filter(this, callback, options);
  }

  /**
   * Apply the GSD peak picking algorithm
   * @param {object} [options] - Options object
   * @param {object} [options.heightFilter = 2] - Filter all objects that are bellow `heightFilter` times the median of the height
   * @return {Array<object>} - List of GSD objects
   */
  getPeaks(options) {
    return getPeaks(this, options);
  }

  /**
   * Calculate tic
   * @param {object} [options = {}] - Options object
   * @param {boolean} [options.force = false] - Force the calculation it it exists
   */
  calculateTic(options = {}) {
    if (!this.getSerie('tic') || options.force) {
      let tic = calculateTic(this);
      this.addSerie('tic', tic, options);
    }
  }

  /**
   * Calculate length and save it in the 'length' serie
   * @param {string} serieName - Name of the serie to make calculation
   * @param {object} [options = {}] - Options object
   * @param {boolean} [options.force = false] - Force the calculation it it exists
   */
  calculateLength(serieName, options = {}) {
    if (!this.getSerie('length') || options.force) {
      let length = calculateLength(this, serieName);
      this.addSerie('length', length, options);
    }
  }

  /**
   * Calculate bpc
   * @param {object} [options = {}] - Options object
   * @param {boolean} [options.force = false] - Force the calculation it it exists
   */
  calculateBpc(options = {}) {
    if (!this.getSerie('bpc') || options.force) {
      let bpc = calculateBpc(this);
      this.addSerie('bpc', bpc, options);
    }
  }

  /**
   * Calculate mass spectrum by filtering for a specific mass
   * @param {number} targetMass - mass for which to extract the spectrum
   * @param {object} [options = {}] - Options object
   * @param {string} [options.serieName='ms'+targetMass] - Name of the serie to make calculation
   * @param {boolean} [options.cache = false] - Retrieve from cache if exists
   * @param {boolean} [options.force = false] - Force replacement of existing serie
   * @param {number} [options.error=0.5] - Allowed error around the targetMass
   * @return {Serie}
   */
  calculateForMass(targetMass, options = {}) {
    const {
      serieName = `ms${targetMass}-${options.error || 0.5}`,
      cache = false
    } = options;
    if (cache && this.hasSerie(serieName)) return this.getSerie(serieName);
    let result = calculateForMass(this, targetMass, options);
    this.addSerie(serieName, result, options);
    return this.getSerie(serieName);
  }

  /**
   * Calculate mass spectrum by filtering for a specific mass
   * @param {string} targetMF - mass for which to extract the spectrum
   * @param {object} [options = {}] - Options object
   * @param {string} [options.serieName='ms'+targetMass] - Name of the serie to make calculation
   * @param {boolean} [options.force = false] - Force replacement of existing serie
   * @param {boolean} [options.cache = false] - Retrieve from cache if exists
   * @param {number} [options.error=0.5] - Allowed error around the targetMass
   * @param {number} [options.ionizations='H+'] - List of allowed ionisation
   * @return {Serie}
   */
  calculateForMF(targetMF, options = {}) {
    const {
      serieName = `ms${targetMF}-${options.ionizations ||
        'H+'}-${options.error || 0.5}`,
      cache = false
    } = options;
    if (cache && this.hasSerie(serieName)) return this.getSerie(serieName);
    let result = calculateForMF(this, targetMF, options);
    this.addSerie(serieName, result, options);
    return this.getSerie(serieName);
  }

  /**
   * Calculates the table of Kovats indexes for the reference spectra
   * @param {object} [options = {}] - Options object
   * @param {number} [options.heightFilter = 100] - Filter all objects that are below heightFilter times the median of the height
   * @param {number} [options.thresholdFactor = 0.005] - Every peak that is below the main peak times this factor will be removed (when is 0 there's no filter)
   * @param {number} [options.maxNumberPeaks = 40] - Maximum number of peaks for each mass spectra (when is Number.MAX_VALUE there's no filter)
   * @param {number} [options.groupWidth = 5] - When find a max can't be another max in a radius of this size
   * @param {boolean} [options.revert = false] - True for convert from Kovats to time, false otherwise
   * @return {{conversionFunction:function(number),kovatsIndexes:Array<object>,peaks:Array<object>}} - Time and value for the Kovats index
   */
  getKovatsRescale(options) {
    return getKovatsRescale(this, options);
  }

  /**
   * Returns an object with the result of the integrations
   * @param {string} name - Name of the serie to integrate
   * @param {Array<Array<number>>} ranges - [[from1, to1], [from2, to2], ...]
   * @param {object} [options = {}] - Options object
   * @param {string|boolean} [options.baseline] - Applies baseline correction
   * @return {[]}
   */
  integrate(name, ranges, options) {
    return integrate(this, name, ranges, options);
  }

  /**
   * Retuns an object with the result of the integrations
   * @param {string} name - Name of the serie to integrate
   * @param {Array<Array<number>>} ranges - [[from1, to1], [from2, to2], ...]
   * @param {object} [options = {}] - Options object
   * @param {object} [options.algorithm = 'slot'] - Decision for merging the peaks
   * @param {object} [options.delta = 1] - Parameter for merging the peaks
   * @return {[]}
   */
  merge(name, ranges, options) {
    return merge(this, name, ranges, options);
  }

  /**
   * Returns information for the closest time
   * @param {number} time - Retention time
   * @return {{index: number, timeBefore: number, timeAfter: number, timeClosest: number, safeIndexBefore: number, safeIndexAfter: number}}
   */
  getClosestTime(time) {
    return getClosestTime(time, this.getTimes());
  }

  /**
   * Return a copy of the chromatogram
   * @return {Chromatogram}
   */
  copy() {
    const json = JSON.parse(JSON.stringify(this));
    return fromJSON(json);
  }

  /**
   * Filter the given serie2D based on it's median value
   * @param {string} serieName
   * @param {object} [options]
   * @param {string} [options.serieName = 'msMedian'] - Name of the new serie
   * @param {number} [options.factor = 2] - The values under the median times this factor are removed
   */
  meanFilter(serieName, options = {}) {
    var serie = meanFilter(this, serieName, options);
    if (options.serieName) {
      this.series[options.serieName] = serie;
    } else {
      this.series.msMedian = serie;
    }
  }

  /**
   * Filter the given serie2D based on the percentage of the highest value
   * @param {string} serieName
   * @param {object} [options]
   * @param {string} [options.serieName = 'msPercentage'] - Name of the new serie
   * @param {number} [options.percentage = 0.1] - The values under the median times this factor are removed
   */
  percentageFilter(serieName, options = {}) {
    var serie = percentageFilter(this, serieName, options);
    if (options.serieName) {
      this.series[options.serieName] = serie;
    } else {
      this.series.msPercentage = serie;
    }
  }
}

Chromatogram.prototype.applyLockMass = applyLockMass;
Chromatogram.prototype.toJSON = toJSON;
Chromatogram.prototype.getClosestData = getClosestData;

/**
 * Given a list of GSD objects returns the weighted mass times abundance
 * @param {Array<object>} peakList - List of GSD objects
 * @param {object} options - Options for the integral filtering
 * @param {number} [options.massPower = 3] - Power applied to the mass values
 * @param {number} [options.intPower = 0.6] - Power applied to the abundance values
 * @param {number} [options.thresholdFactor = 0] - Every peak that it's bellow the main peak times this factor fill be removed (when is 0 there's no filter)
 * @param {number} [options.maxNumberPeaks = Number.MAX_VALUE] - Maximum number of peaks for each mass spectra (when is Number.MAX_VALUE there's no filter)
 * @param {number} [options.groupWidth = 0] - When find a max can't be another max in a radius of this size
 * @return {Array<object>} - List of mass and weighted mass times abundance objects
 */
function vectorify(peakList, options = {}) {
  const { massPower = 3, intPower = 0.6 } = options;
  let filter = (options.thresholdFactor || options.maxNumberPeaks || options.groupWidth);

  let vector = new Array(peakList.length);
  if (filter) {
    const filterOptions = {
      thresholdFactor: options.thresholdFactor,
      maxNumberPeaks: options.maxNumberPeaks,
      groupWidth: options.groupWidth
    };

    for (let i = 0; i < peakList.length; ++i) {
      let len = peakList[i].ms.x.length;
      vector[i] = {
        x: peakList[i].ms.x,
        y: new Array(len)
      };
      for (let j = 0; j < len; ++j) {
        vector[i].y[j] = Math.pow(peakList[i].ms.x[j], massPower) * Math.pow(peakList[i].ms.y[j], intPower);
      }

      vector[i] = massFilter(vector[i], filterOptions);
    }
  } else {
    for (let i = 0; i < peakList.length; ++i) {
      let len = peakList[i].ms.x.length;
      vector[i] = {
        x: peakList[i].ms.x,
        y: new Array(len)
      };
      for (let j = 0; j < len; ++j) {
        vector[i].y[j] = Math.pow(peakList[i].ms.x[j], massPower) * Math.pow(peakList[i].ms.y[j], intPower);
      }
    }
  }

  return vector;
}

/**
 * Cosine similarity between two MS spectra
 * @param {Array<number>} ms1x - Array of mass values for the first spectra
 * @param {Array<number>} ms1y - Array of weighted abundance values for the first spectra
 * @param {Array<number>} ms2x - Array of mass values for the second spectra
 * @param {Array<number>} ms2y - Array of weighted abundance values for the second spectra
 * @return {number} - Similarity between two MS spectra
 */
function cosine(ms1x, ms1y, ms2x, ms2y) {
  let index1 = 0;
  let index2 = 0;

  let product = 0;
  let norm1 = 0;
  let norm2 = 0;

  while ((index1 < ms1x.length) || (index2 < ms2x.length)) {
    let w1 = ms1y[index1];
    let w2 = ms2y[index2];
    if (index2 === ms2x.length || ms1x[index1] < ms2x[index2]) {
      norm1 += w1 * w1;
      ++index1;
    } else if (index1 === ms1x.length || ms2x[index2] < ms1x[index1]) {
      norm2 += w2 * w2;
      ++index2;
    } else {
      product += w1 * w2;
      norm1 += w1 * w1;
      norm2 += w2 * w2;
      ++index1;
      ++index2;
    }
  }

  var norm1Norm2 = norm1 * norm2;
  if (norm1Norm2 === 0) {
    return 0;
  } else {
    return (product * product) / (norm1Norm2);
  }
}

/**
 * Preprocessing task over the signals
 * @ignore
 * @param {Chromatogram} chromatography - Chromatogram to process
 * @param {object} [options] - Options object (same as spectraComparison)
 * @return {{peaks: Array<object>, integratedMs: Array<object>, vector: Array<object>}} - Array of peaks, integrated mass spectra and weighted mass spectra
 */
function preprocessing(chromatography, options) {
  // peak picking
  let peaks = getPeaks(chromatography, options);
  peaks = peaks.sort((a, b) => a.index - b.index);

  // integrate mass in the peaks
  let ms = chromatography.getSerie('ms').data;
  let integratedMs = massInPeaks(peaks, ms, options);
  let vector = vectorify(integratedMs, options);

  return {
    peaks,
    integratedMs,
    vector
  };
}

const defaultOption = {
  thresholdFactor: 0,
  maxNumberPeaks: Number.MAX_VALUE,
  groupWidth: 0,
  heightFilter: 2,
  massPower: 3,
  intPower: 0.6,
  similarityThreshold: 0.7
};

/**
 * Returns the most similar peaks between two GC/MS and their similarities
 * @param {Chromatogram} chrom1 - First chromatogram
 * @param {Chromatogram} chrom2 - Second chromatogram
 * @param {object} [options] - Options object
 * @param {number} [options.thresholdFactor = 0] - Every peak that it's bellow the main peak times this factor fill be removed (when is 0 there's no filter)
 * @param {number} [options.maxNumberPeaks = Number.MAX_VALUE] - Maximum number of peaks for each mass spectra (when is Number.MAX_VALUE there's no filter)
 * @param {number} [options.groupWidth = 0] - When find a max can't be another max in a radius of this size
 * @param {number} [options.heightFilter = 2] - Filter all objects that are bellow `heightFilter` times the median of the height
 * @param {number} [options.massPower = 3] - Power applied to the mass values
 * @param {number} [options.intPower = 0.6] - Power applied to the abundance values
 * @param {number} [options.similarityThreshold = 0.7] - Minimum similarity value to consider them similar
 * @return {object} - Most similar peaks and their similarities:
 * * `peaksFirst`: Array of peaks, integrated mass spectra and weighted mass spectra for the first chromatogram
 * * `peaksSecond`: Array of peaks, integrated mass spectra and weighted mass spectra for the second chromatogram
 * * `peaksSimilarity`: Array of similarities (number)
 */
function spectraComparison(chrom1, chrom2, options) {
  options = Object.assign({}, defaultOption, options);

  // peak picking
  let reference = preprocessing(chrom1, options);
  let sample = preprocessing(chrom2, options);

  // similarity between MS
  const len = Math.max(sample.peaks.length, reference.peaks.length);
  let similarityPeaks = {
    chrom1: new Array(len),
    chrom2: new Array(len),
    similarity: new Array(len)
  };
  let similarLen = 0;

  // Similarity matrix
  for (let i = 0; i < sample.peaks.length; ++i) {
    let max$$1 = { similarity: -3 };
    let biggerCounter = 0;
    for (let j = 0; j < reference.peaks.length; ++j) {
      let sim = cosine(sample.vector[i].x, sample.vector[i].y, reference.vector[j].x, reference.vector[j].y);

      if (sim > options.similarityThreshold && sim > max$$1.similarity) {
        max$$1 = {
          similarity: sim,
          chrom1: reference.peaks[j],
          chrom2: sample.peaks[i]
        };
      }
      if (sim > options.similarityThreshold) {
        ++biggerCounter;
      }
    }
    if (biggerCounter === 1) {
      similarityPeaks.chrom1[similarLen] = max$$1.chrom1;
      similarityPeaks.chrom2[similarLen] = max$$1.chrom2;
      similarityPeaks.similarity[similarLen++] = max$$1.similarity;
    }
  }
  similarityPeaks.chrom1.length = similarLen;
  similarityPeaks.chrom2.length = similarLen;

  let duplicates = {};
  for (let i = 0; i < similarLen; ++i) {
    if ({}.hasOwnProperty.call(duplicates, similarityPeaks.chrom1[i].x)) {
      duplicates[similarityPeaks.chrom1[i].x].push(i);
    } else {
      duplicates[similarityPeaks.chrom1[i].x] = [i];
    }
  }

  let peaksChrom1 = [];
  let peaksChrom2 = [];
  let peaksSimilarity = [];
  for (let val in duplicates) {
    if (duplicates[val].length === 1) {
      peaksChrom1.push(similarityPeaks.chrom1[duplicates[val][0]]);
      peaksChrom2.push(similarityPeaks.chrom2[duplicates[val][0]]);
      peaksSimilarity.push(similarityPeaks.similarity[duplicates[val][0]]);
    }
  }

  return {
    peaksFirst: peaksChrom1,
    peaksSecond: peaksChrom2,
    peaksSimilarity: peaksSimilarity,
  };
}

/**
 * Aligns the time of the sample based on the regression with his reference value
 * @param {Array<object>} reference - Array of peaks, integrated mass spectra and weighted mass spectra for the reference chromatogram
 * @param {Array<object>} sample - Array of peaks, integrated mass spectra and weighted mass spectra for the sample chromatogram
 * @param {object} [options] - Options object
 * @param {boolean} [options.computeQuality = false] - Calculate the quality of the regression
 * @param {number} [options.polynomialDegree = 3] - Degree of the polynomial regression
 * @return {object} - The scaled spectra:
 * * `scaleRegression`: The regression function to make the regression
 * * `stringFormula`: Regression equation
 * * `r2`: R2 quality number
 * * `error`: Vector of the difference between the spected value and the actual shift value
 */
function scaleAlignment(reference, sample, options = {}) {
  const {
    computeQuality = false,
    polynomialDegree = 3
  } = options;
  let referenceTime = reference.map((val) => val.x);
  let sampleTime = sample.map((val) => val.x);

  const regression = new Regression(sampleTime, referenceTime, polynomialDegree);

  let error = new Array(sample.length);
  for (var i = 0; i < sample.length; i++) {
    error[i] = reference[i].x - regression.predict(sample[i].x);
  }

  let ans = {
    scaleRegression: regression
  };

  if (computeQuality) {
    let score = regression.score(sampleTime, referenceTime);
    ans.r2 = score.r2;
    ans.error = error;
  }
  return ans;
}

/**
 * Creates a new Chromatogram element based in a JCAMP string
 * @param {string} jcamp - String containing the JCAMP data
 * @return {Chromatogram} - New class element with the given data
 */
function fromJcamp(jcamp) {
  const data = jcampconverter.convert(jcamp, { chromatogram: true }).chromatogram;
  return fromJSON(data);
}

/**
 * Creates a new Chromatogram element based in a Txt string
 * @param {string} text - String containing the data as CSV or TSV
 * @param {object} [options] - Options object for the parser
 * @return {Chromatogram} - New class element with the given data
 */
function fromText(text, options) {
  options = Object.assign({}, options, { arrayType: 'xxyy' });
  const data = xyParser.parseXY(text, options);

  const time = data[0];
  let series = {
    intensity: data[1]
  };

  return new Chromatogram(time, series);
}

function fromNetCDF(netcdf) {
  return fromJSON(netcdfJSON(netcdf));
}

function fromMzML(xml, kind = 'mzData') {
  switch (kind) {
    case 'mzData':
      return fromJSON(mzData(xml));
    default:
      throw new Error(`Unable to parse from "${kind}" format`);
  }
}

exports.Chromatogram = Chromatogram;
exports.massInPeaks = massInPeaks;
exports.vectorify = vectorify;
exports.massFilter = massFilter;
exports.spectraComparison = spectraComparison;
exports.scaleAlignment = scaleAlignment;
exports.kovats = kovats;
exports.getKovatsTable = getKovatsTable;
exports.kovatsConversionFunction = kovatsConversionFunction;
exports.rescaleTime = rescaleTime;
exports.getKovatsRescale = getKovatsRescale;
exports.cosine = cosine;
exports.meanFilter = meanFilter;
exports.percentageFilter = percentageFilter;
exports.fromJcamp = fromJcamp;
exports.fromJSON = fromJSON;
exports.fromText = fromText;
exports.fromNetCDF = fromNetCDF;
exports.fromMzML = fromMzML;
exports.integrate = integrate;
exports.merge = merge;
exports.getPeaks = getPeaks;
